/* eslint-disable dot-notation */
import React, { FC, useCallback, useState } from 'react';
import { YMaps, Map, Placemark } from 'react-yandex-maps';
import cn from 'classnames';
import Collapse from 'antd/lib/collapse';

import { Car } from '@lib/azgaz-ui/connect';
import { getCarDealerOffices } from '@/selectors';
import { CarOfficeCard, Title, Icon } from '../index';
import { TOffice } from '../CarOfficeCard';

import style from './style.module.scss';

const defaultIcon =
  "data:image/svg+xml,%3Csvg width='65' height='80' viewBox='0 0 65 80' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg filter='url(%23filter0_d)'%3E%3Cpath d='M32.4958 70C32.4958 70 10 47.3125 10 32.2812C10 19.9939 20.0901 10 32.4958 10C44.9099 10 55 19.9939 55 32.2812C55 47.3125 32.4958 70 32.4958 70Z' fill='white'/%3E%3Cpath d='M43 36.5816C43 36.5816 41.0118 37.1424 37.2219 39.5104C33.8047 41.6291 32.5 43 32.5 43L22 35.7715C22 35.7715 26.4112 32.5312 30.0769 35.0237L35.0473 38.451L35.6686 38.0772L22 28.4184C22 28.4184 23.9882 27.8576 27.7781 25.4896C31.1953 23.3709 32.5 22 32.5 22L43 29.2908C43 29.2908 38.5888 32.5312 34.9231 30.0386L29.9527 26.6113L29.3314 26.9852L43 36.5816Z' fill='%23E20109'/%3E%3C/g%3E%3Cdefs%3E%3Cfilter id='filter0_d' x='0' y='0' width='65' height='80' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'%3E%3CfeFlood flood-opacity='0' result='BackgroundImageFix'/%3E%3CfeColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'/%3E%3CfeOffset/%3E%3CfeGaussianBlur stdDeviation='5'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0.192157 0 0 0 0 0.270588 0 0 0 0.2 0'/%3E%3CfeBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow'/%3E%3CfeBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow' result='shape'/%3E%3C/filter%3E%3C/defs%3E%3C/svg%3E%0A";
const activeIcon =
  "data:image/svg+xml,%3Csvg width='65' height='80' viewBox='0 0 65 80' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cg filter='url(%23filter0_d)'%3E%3Cpath d='M32.4958 70C32.4958 70 10 47.3125 10 32.2812C10 19.9939 20.0901 10 32.4958 10C44.9099 10 55 19.9939 55 32.2812C55 47.3125 32.4958 70 32.4958 70Z' fill='%23E20109'/%3E%3Cpath d='M43 36.5816C43 36.5816 41.0118 37.1424 37.2219 39.5104C33.8047 41.6291 32.5 43 32.5 43L22 35.7715C22 35.7715 26.4112 32.5312 30.0769 35.0237L35.0473 38.451L35.6686 38.0772L22 28.4184C22 28.4184 23.9882 27.8576 27.7781 25.4896C31.1953 23.3709 32.5 22 32.5 22L43 29.2908C43 29.2908 38.5888 32.5312 34.9231 30.0386L29.9527 26.6113L29.3314 26.9852L43 36.5816Z' fill='white'/%3E%3C/g%3E%3Cdefs%3E%3Cfilter id='filter0_d' x='0' y='0' width='65' height='80' filterUnits='userSpaceOnUse' color-interpolation-filters='sRGB'%3E%3CfeFlood flood-opacity='0' result='BackgroundImageFix'/%3E%3CfeColorMatrix in='SourceAlpha' type='matrix' values='0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0'/%3E%3CfeOffset/%3E%3CfeGaussianBlur stdDeviation='5'/%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 0 0 0 0 0 0.192157 0 0 0 0 0.270588 0 0 0 0.2 0'/%3E%3CfeBlend mode='normal' in2='BackgroundImageFix' result='effect1_dropShadow'/%3E%3CfeBlend mode='normal' in='SourceGraphic' in2='effect1_dropShadow' result='shape'/%3E%3C/filter%3E%3C/defs%3E%3C/svg%3E%0A";
const iconSize = [45, 60];

type TProps = {
  car: Car;
};

const CarOfficesMap: FC<TProps> = ({ car }) => {
  const { offices, officesTitle } = getCarDealerOffices(car);
  const [centerMap, setCenterMap] = useState(offices[0]?.location?.coordinates);
  const [activePlacemarkId, setActivePlacemarkId] = useState(0);

  const setActiveOffice = (id: number) => {
    setActivePlacemarkId(id);
    setCenterMap(offices[id]?.location?.coordinates);
  };

  const placemarkClickHandler = useCallback((e: Record<string, any>) => {
    const { id } = e.originalEvent.target.properties['_data'];

    setActiveOffice(id);
  }, []);

  return (
    <Collapse
      className={cn(style.collapse, 'mb-5 collapse-default-theme')}
      expandIcon={() => <Icon name="circled-angle" />}
      expandIconPosition="right"
      defaultActiveKey={1}
    >
      <Collapse.Panel
        key={1}
        forceRender
        header={
          <Title className="my-2" tag="h2">
            {officesTitle}
          </Title>
        }
      >
        <div className={style.wrapper}>
          <YMaps>
            <CarOfficeCard
              className={style.carOfficeCard}
              offices={offices}
              activeOfficeId={activePlacemarkId}
              setActiveOffice={setActiveOffice}
            />
            <Map
              className={style.map}
              state={{
                center: centerMap,
                zoom: 9,
              }}
            >
              {offices.map((office: TOffice, idx: number) => (
                <Placemark
                  // eslint-disable-next-line react/no-array-index-key
                  key={idx}
                  defaultGeometry={office.location.coordinates}
                  options={{
                    iconLayout: 'default#image',
                    iconImageHref:
                      activePlacemarkId === idx ? activeIcon : defaultIcon,
                    iconImageSize: iconSize,
                  }}
                  properties={{
                    id: idx,
                  }}
                  onClick={placemarkClickHandler}
                />
              ))}
            </Map>
          </YMaps>
        </div>
      </Collapse.Panel>
    </Collapse>
  );
};

export default CarOfficesMap;
